import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
} from "react";
import { io } from "socket.io-client";
import { socketURL } from "../config";
import { handleScoreAPI } from "../services/resumeService";
import { showAppAlert } from "../store/reducers/appreducer";

const UploadContext = createContext();

const processingState = {
  isProcessing: false,
  jobId: null,
  resumes: [],
  processingComplete: false,
  scoringAttempted: false,
  scoringStartTime: null,
  socketConnected: false,
  currentProgress: 0,
};

export const UploadProvider = ({ children }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [socketConnected, setSocketConnected] = useState(false);
  const [scoringComplete, setScoringComplete] = useState(false);
  const [displayText, setDisplayText] = useState("Initializing...");

  const socketRef = useRef(null);
  const dispatchRef = useRef(null);
  const scoringTimerRef = useRef(null);



  const initiateScoring = async (jobId, dispatch) => {
    if (!jobId || processingState.scoringAttempted) return null;

    processingState.scoringAttempted = true;
    processingState.scoringStartTime = Date.now();

    try {
      // Update to 90% when scoring starts
      processingState.currentProgress = 90;
      setProgress(90);
      const result = await handleScoreAPI(jobId);

      processingState.currentProgress = 100;
      setProgress(100);

      if (dispatch) {
        dispatch(
          showAppAlert({
            show: true,
            alertType: "success",
            alertMessage:
              "Resume scoring completed. Please go to View Report section for results.",
          })
        );
      }

      processingState.processingComplete = true;
      processingState.isProcessing = false;
      setScoringComplete(true);
      setDisplayText("Scoring Completed!");

      // Disconnect socket only when processing is fully complete
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocketConnected(false);
        processingState.socketConnected = false;
        console.log("Socket disconnected after processing completion");
      }

      return result;
    } catch (error) {
      if (error.message === "No Unscored Resumes found") {
        processingState.scoringAttempted = false;
        return null;
      }

      if (dispatch) {
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage: error.message || "Failed to score resumes.",
          })
        );
      }

      return null;
    }
  };

  const startProcessing = (jobId, newResumes, dispatch) => {
    if (!jobId) return;

    // Reset state for new processing
    processingState.jobId = jobId;
    processingState.resumes = newResumes;
    processingState.isProcessing = true;
    processingState.processingComplete = false;
    processingState.scoringAttempted = false;
    processingState.currentProgress = 0;
    processingState.socketConnected = false;

    setScoringComplete(false);
    setProgress(0);
    dispatchRef.current = dispatch;

    setDisplayText("Resume Processing Started, Please Wait!");

    if (!socketRef.current) {
      socketRef.current = io(socketURL, {
        withCredentials: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      socketRef.current.on("connect", () => {
        setSocketConnected(true);
        processingState.socketConnected = true;
        console.log("Socket connected");
      });

      socketRef.current.on("task_process", (data) => {
        console.log("Task process data:", data);
        if (data.job_id === jobId) {
          if (data.status === "In Process") {
            const totalResumes = processingState.resumes.length;
            const processedResumes = data.processed_count ? parseInt(data.processed_count) : 0;
            if (totalResumes > 0) {
              // Calculate progress percentage 
              const progressPercentage = Math.min(
                Math.round((processedResumes / totalResumes) * 80),
                80
              );

              console.log(`Progress update: ${processedResumes}/${totalResumes} = ${progressPercentage}%`);
              processingState.currentProgress = progressPercentage;
              setProgress(progressPercentage);

            }

          } else if (data.status === "Finished") {
            // When processing is finished but scoring hasn't started yet,
            processingState.currentProgress = 80;
            setProgress(80);
            setDisplayText("Processing complete. Starting scoring...");

            scoringTimerRef.current = setTimeout(() => {
              // Update to 90% when scoring starts
              processingState.currentProgress = 90;
              setProgress(90);
              setDisplayText("Scoring resumes...");

              initiateScoring(jobId, dispatchRef.current)
                .then((result) => {
                  if (result) {
                    // Only set to 100% when scoring is actually complete
                    processingState.currentProgress = 100;
                    setProgress(100);
                    setDisplayText("Scoring Completed!");
                  }
                });
            }, 1000); // Reduced delay before scoring starts
          }
        }
      });

      socketRef.current.on("disconnect", () => {
        setSocketConnected(false);
        processingState.socketConnected = false;
        console.log("Socket disconnected");
        if (
          processingState.isProcessing &&
          !processingState.processingComplete
        ) {
          scoringTimerRef.current = setTimeout(() => {
            initiateScoring(jobId, dispatchRef.current).then(() => {
              setDisplayText("Scoring Completed!");
            });
          }, 10000);
        }
      });
    }
  };

  const forceScoring = async () => {
    if (processingState.jobId && processingState.isProcessing) {
      setDisplayText("Forcing Resume Scoring, Please Wait!");
      processingState.scoringAttempted = false;

      const result = await initiateScoring(
        processingState.jobId,
        dispatchRef.current
      );
      if (result) {
        setDisplayText("Scoring Completed!");
      }
    }
  };

  const isCurrentlyProcessing = () => {
    return processingState.isProcessing && !processingState.processingComplete;
  };

  const getCurrentJobId = () => {
    return processingState.jobId;
  };

  const getCurrentProgress = () => {
    return processingState.currentProgress;
  };

  const resetProcessingState = () => {
    // Disconnect socket only if we're done with processing
    if (processingState.processingComplete && socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
      processingState.socketConnected = false;
      console.log("Socket disconnected during reset (processing was complete)");
    }

    processingState.isProcessing = false;
    processingState.jobId = null;
    processingState.resumes = [];
    processingState.processingComplete = false;
    processingState.scoringAttempted = false;
    processingState.scoringStartTime = null;
    processingState.socketConnected = false;
    processingState.currentProgress = 0;

    setScoringComplete(false);
    setProgress(0);
    setDisplayText("Initializing...");
    setSocketConnected(false);

    if (scoringTimerRef.current) {
      clearTimeout(scoringTimerRef.current);
    }
  };

  useEffect(() => {
    return () => {
      if (scoringTimerRef.current) {
        clearTimeout(scoringTimerRef.current);
      }
      // disconnectSocket();
      if (!processingState.isProcessing && socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        console.log("Socket disconnected on unmount (processing was complete)");
      }
    };
  }, []);

  return (
    <UploadContext.Provider
      value={{
        uploading,
        setUploading,
        progress,
        setProgress,
        socketConnected,
        scoringComplete,
        setScoringComplete,
        displayText,
        setDisplayText,
        startProcessing,
        forceScoring,
        isCurrentlyProcessing,
        getCurrentJobId,
        getCurrentProgress,
        resetProcessingState,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export const useUploadContext = () => {
  return useContext(UploadContext);
};
